import React, { useCallback, useEffect, useRef, useState } from "react";
import { authFetch } from "../../HttpClient";

export const PAGE_SIZE = 25;

// Les 3 étapes du workflow LM suivies ici (voir analyse validée avec
// Jonathan) : brouillon (assistant), soumission (assistant), validation +
// envoi pour signature (comptable — résolu côté backend vers son compte
// MyAlfa via lmValidationController.js).
export const LM_ACTIONS = "LM_BROUILLON,LM_SOUMIS,LM_VALIDE";

const ACTION_BADGES = {
  LM_BROUILLON: { label: "Brouillon", className: "bg-gray-100 text-gray-600" },
  LM_SOUMIS: { label: "Soumis", className: "bg-amber-50 text-amber-700" },
  LM_VALIDE: { label: "Validé", className: "bg-emerald-50 text-emerald-700" },
};

// `initialData` ({ total, items }) est la page 1 déjà préchargée par
// SuperAdmin.js pendant que la vue d'ensemble était affichée — même
// principe que ConnexionsTab.js : ouverture instantanée, pas de re-fetch ni
// de skeleton au premier rendu.
function LmTab({ from, to, initialData }) {
  const [items, setItems] = useState(initialData?.items || []);
  const [total, setTotal] = useState(initialData?.total || 0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!initialData);
  const skipNextFetch = useRef(Boolean(initialData));

  useEffect(() => {
    setPage(1);
  }, [from, to]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: LM_ACTIONS,
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/super-admin/audit-logs?${params.toString()}`
      );
      const data = await response.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Erreur lors de la récupération des LM :", error);
    } finally {
      setLoading(false);
    }
  }, [page, from, to]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          Lettres de mission
        </h2>
        <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
          {total}
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full" style={{ fontSize: "0.72rem" }}>
          <thead>
            <tr className="bg-[#6e1a36] text-xs">
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Étape
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Dossier
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Collaborateur
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Date &amp; heure
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-3 py-3" colSpan={4}>
                    <div className="h-3 bg-gray-100 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-8 text-center text-gray-400"
                >
                  Aucune activité LM enregistrée sur cette période.
                </td>
              </tr>
            ) : (
              items.map((log) => {
                const badge = ACTION_BADGES[log.action] || {
                  label: log.action,
                  className: "bg-gray-100 text-gray-600",
                };
                return (
                  <tr key={log.id}>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full font-semibold ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {log.description || "—"}
                    </td>
                    <td className="px-3 py-2.5 font-medium text-slate-900">
                      {log.user?.name} {log.user?.lastName}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {new Date(log.createdAt).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Précédent
          </button>
          <span className="text-xs text-gray-400">
            Page {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}

export default LmTab;
